import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged, 
  setPersistence, 
  browserLocalPersistence,
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as fbSignOut,
  sendPasswordResetEmail,
  getAuth
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs, updateDoc, addDoc, increment } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import type { User } from '../types';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, responderType: 'Firefighter'|'Police'|'Other', agency?: string, accessCode?: string, agreedToTerms?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // console.log('🔄 AuthProvider state:', { hasUser: !!user, loading, hasError: !!error });

  useEffect(() => {
    let settled = false;

    // Fallback so the app doesn't hang forever if listener never fires
    const failOpen = setTimeout(() => {
      if (!settled) {
        setLoading(false);
      }
    }, 5000);
    
    // Ensure persistence once
    setPersistence(auth, browserLocalPersistence).catch(() => {
      // Persistence setting failed, continue anyway
    });
    
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      settled = true;
      clearTimeout(failOpen);
      
      try {
        if (!fbUser) {
          setUser(null);
          setError(null);
          return;
        }
        
        // Try to load profile; if it fails, still expose minimal user so guards pass
        const userRef = doc(db, 'users', fbUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data() as any;
          
          const fullUser: User = {
            uid: fbUser.uid,
            displayName: userData.displayName ?? fbUser.displayName ?? null,
            email: fbUser.email,
            photoURL: fbUser.photoURL ?? null,
            role: userData.role ?? 'user',
            responderType: userData.responderType ?? 'Other',
            agency: userData.agency ?? null,
            anonymousDefault: userData.anonymousDefault ?? true,
            settings: userData.settings ?? { 
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
              pushEnabled: true 
            },
            createdAt: userData.createdAt?.toDate?.() ?? userData.createdAt ?? null,
            updatedAt: userData.updatedAt?.toDate?.() ?? userData.updatedAt ?? null,
          };
          
          setUser(fullUser);
        } else {
          console.log('🔄 No user profile in Firestore, creating new one...');
          
          const newUserData = {
            displayName: fbUser.displayName ?? null,
            email: fbUser.email,
            photoURL: fbUser.photoURL ?? null,
            role: 'user' as const,
            responderType: 'Other' as const,
            anonymousDefault: true,
            agency: null,
            settings: { 
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
              pushEnabled: true 
            },
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          await setDoc(userRef, newUserData);
          
          const newUser: User = {
            uid: fbUser.uid,
            ...newUserData,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          
          setUser(newUser);
        }
        
        setError(null);
        console.log('✅ User profile loaded successfully');
        
      } catch (err: any) {
        console.error('❌ Auth bootstrap error:', err);
        
        // Expose minimal user so route guards don't loop if Firestore read is blocked
        if (auth.currentUser) {
          console.log('⚠️ Firestore error, but Firebase user exists - creating minimal user');
          
          const minimalUser: User = {
            uid: auth.currentUser.uid,
            displayName: auth.currentUser.displayName ?? null,
            email: auth.currentUser.email,
            photoURL: auth.currentUser.photoURL ?? null,
            role: 'user' as const,
            responderType: 'Other' as const,
            anonymousDefault: true,
            agency: null,
            settings: { 
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
              pushEnabled: true 
            },
            createdAt: null,
            updatedAt: null,
          };
          
          setUser(minimalUser);
        } else {
          setUser(null);
        }
        
        setError('Failed to load profile');
      } finally {
        setLoading(false);
        console.log('🔄 Auth loading complete');
      }
    });
    
    return () => {
      clearTimeout(failOpen);
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      
      // Ensure claims are fresh before navigation
      await getAuth().currentUser?.getIdToken(true);
      await getAuth().currentUser?.reload();
      
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    displayName: string, 
    responderType: 'Firefighter' | 'Police' | 'Other', 
    agency?: string,
    accessCode?: string,
    agreedToTerms?: boolean
  ) => {
    setError(null);
    
    try {
      const { user: fbUser } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Validate access code if provided
      let accessCodeData = null;
      if (accessCode && accessCode.trim()) {
        const accessCodesQuery = query(
          collection(db, 'accessCodes'),
          where('code', '==', accessCode.toUpperCase().trim())
        );
        const accessCodesSnap = await getDocs(accessCodesQuery);

        if (accessCodesSnap.empty) {
          throw new Error('Invalid access code. Please check with your department administrator.');
        }

        const accessCodeDoc = accessCodesSnap.docs[0];
        const docData = accessCodeDoc.data();
        accessCodeData = {
          id: accessCodeDoc.id,
          code: docData.code,
          departmentName: docData.departmentName,
          departmentContact: docData.departmentContact,
          contactEmail: docData.contactEmail,
          description: docData.description,
          maxUses: docData.maxUses,
          currentUses: docData.currentUses,
          expiresAt: docData.expiresAt?.toDate(),
          createdAt: docData.createdAt?.toDate(),
          updatedAt: docData.updatedAt?.toDate(),
          isActive: docData.isActive,
          allowedModules: docData.allowedModules,
          createdBy: docData.createdBy,
          lastUsedAt: docData.lastUsedAt?.toDate(),
          trackingEnabled: docData.trackingEnabled,
          customFields: docData.customFields
        };

        // Validate access code
        if (!accessCodeData.isActive) {
          throw new Error('This access code has been disabled. Contact your administrator.');
        }

        if (accessCodeData.expiresAt && accessCodeData.expiresAt < new Date()) {
          throw new Error('This access code has expired. Contact your administrator for a new one.');
        }

        if (accessCodeData.maxUses && accessCodeData.currentUses >= accessCodeData.maxUses) {
          throw new Error('This access code has reached its usage limit. Contact your administrator.');
        }

        console.log('✅ Access code validated');
      }
      
      console.log('📝 Creating user profile in Firestore...');
      const userRef = doc(db, 'users', fbUser.uid);
      const userData = {
        displayName, 
        email, 
        photoURL: null,
        role: 'user' as const, 
        responderType, 
        agency: agency || undefined,
        anonymousDefault: true,
        settings: { 
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, 
          pushEnabled: true 
        },
        agreedToTerms: agreedToTerms || false,
        termsAgreedAt: agreedToTerms ? serverTimestamp() : null,
        createdAt: serverTimestamp(), 
        updatedAt: serverTimestamp(),
      } as any;

      // Add access code info if validated
      if (accessCodeData) {
        userData.accessCodeId = accessCodeData.id;
        userData.departmentName = accessCodeData.departmentName;
        userData.departmentType = accessCodeData.customFields?.departmentType;
        userData.region = accessCodeData.customFields?.region;
      }

      await setDoc(userRef, userData);

      // Update access code usage if validated
      if (accessCodeData) {
        console.log('📊 Updating access code usage...');
        await updateDoc(doc(db, 'accessCodes', accessCodeData.id), {
          currentUses: increment(1),
          lastUsedAt: new Date(),
          updatedAt: new Date()
        });

        // Create usage record
        await addDoc(collection(db, 'accessCodeUsage'), {
          accessCodeId: accessCodeData.id,
          userId: fbUser.uid,
          userEmail: email,
          userName: displayName,
          departmentName: accessCodeData.departmentName,
          usedAt: new Date(),
          ipAddress: '', // Could be populated from request
          userAgent: navigator.userAgent,
          modulesStarted: [],
          modulesCompleted: [],
          totalTimeSpent: 0,
          lastActivity: new Date(),
          loginCount: 1,
          journalEntries: 0
        });

        console.log('✅ Access code usage recorded');
      }
      
      console.log('📝 Refreshing token...');
      await getAuth().currentUser?.getIdToken(true);
      await getAuth().currentUser?.reload();
      
      console.log('✅ Sign up successful');
    } catch (err: any) {
      console.error('❌ Sign up error:', err);
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    console.log('🚪 AuthProvider signOut called');
    try {
      await fbSignOut(auth);
      console.log('✅ Sign out successful');
    } catch (err: any) {
      console.error('❌ Sign out error:', err);
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    setError(null);
    
    try {
      await sendPasswordResetEmail(auth, email, {
        url: 'https://gritgrowthgratitude.app/auth',
        handleCodeInApp: false
      });
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const value = useMemo(() => ({ 
    user, 
    loading, 
    error, 
    signIn, 
    signUp, 
    signOut,
    resetPassword
  }), [user, loading, error]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
